import type { ReactNode } from 'react';
import HierarchyBreadcrumb, { type BreadcrumbItem } from '../HierarchyBreadcrumb';

export interface DetailFact {
  label: ReactNode;
  value: ReactNode;
  /** Render the value as an LTR island (emails, phones, codes). */
  ltr?: boolean;
}

interface DetailHeaderProps {
  title: ReactNode;
  /** Status / type badges shown next to the title (keep to 1–2). */
  badges?: ReactNode;
  /** One short line under the title. */
  subtitle?: ReactNode;
  /** 1–4 key facts rendered as a quiet definition list. */
  facts?: DetailFact[];
  /** One primary action (last) plus secondary actions / an overflow menu. */
  actions?: ReactNode;
  /** Where the record lives (hierarchy). "חזרה" is the TopBar's, on every page. */
  breadcrumb?: BreadcrumbItem[];
  children?: ReactNode;
}

/**
 * Detail page header (DESIGN_SYSTEM.md §3–4, §10): breadcrumb → title with
 * one key status → up to four facts, actions at the end. No surface of its
 * own — it sits on the page background so the content below reads as the
 * only white layer.
 */
export default function DetailHeader({
  title, badges, subtitle, facts, actions, breadcrumb, children,
}: DetailHeaderProps) {
  return (
    <header className="space-y-3">
      {breadcrumb && <HierarchyBreadcrumb items={breadcrumb} className="min-w-0" />}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 flex-1 basis-64">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="text-2xl font-semibold tracking-tight text-surface-900 break-words min-w-0">{title}</h1>
            {badges && <div className="flex flex-wrap items-center gap-1.5">{badges}</div>}
          </div>
          {subtitle && <p className="mt-1 text-sm text-surface-600 max-w-3xl">{subtitle}</p>}
        </div>
        {/* QA-07 — max-w-full: the action group never grows past the row (at
            390 px the project page's four buttons scrolled the page 25 px
            sideways); it wraps onto a second line instead. */}
        {actions && <div className="flex flex-wrap items-center gap-2 flex-shrink-0 max-w-full">{actions}</div>}
      </div>
      {facts && facts.length > 0 && (
        <dl className="flex flex-wrap gap-x-8 gap-y-3">
          {facts.map((f, i) => (
            <div key={i} className="min-w-0">
              <dt className="text-xs text-surface-500">{f.label}</dt>
              <dd className="mt-0.5 text-sm text-surface-900 break-words" dir={f.ltr ? 'ltr' : undefined}>
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {children}
    </header>
  );
}
